import { collection, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

/**
 * Purge stale session state documents (older than N minutes).
 * Returns the number of sessions deleted.
 */
export const purgeStaleSessionStates = async ({ olderThanMinutes = 30 }) => {
  if (!db) throw new Error('Database not initialized');
  
  const ref = collection(db, 'sessionStates');
  const snap = await getDocs(ref);
  
  if (snap.empty) return 0;
  
  const cutoffMs = Date.now() - (olderThanMinutes * 60 * 1000);
  let count = 0;
  const toDelete = [];
  
  snap.forEach(d => {
    const data = d.data() || {};
    const updatedAt = data.updatedAt;
    let ms = null;
    
    try {
      if (updatedAt && typeof updatedAt.toMillis === 'function') {
        ms = updatedAt.toMillis();
      } else if (updatedAt && updatedAt.seconds) {
        ms = updatedAt.seconds * 1000;
      } else if (updatedAt) {
        const date = new Date(updatedAt);
        if (!isNaN(date.getTime())) ms = date.getTime();
      }
    } catch {}
    
    // Delete if stale or missing timestamp
    if (!ms || ms < cutoffMs) {
      toDelete.push(d.ref);
    }
  });
  
  if (toDelete.length === 0) return 0;
  
  // Batch delete (respect 500 limit)
  const chunkSize = 450;
  for (let i = 0; i < toDelete.length; i += chunkSize) {
    const batch = writeBatch(db);
    const slice = toDelete.slice(i, i + chunkSize);
    slice.forEach(ref => batch.delete(ref));
    await batch.commit();
    count += slice.length;
  }
  
  return count;
};

/**
 * List all active session states with user info.
 * Returns array of { userId, email, displayName, activeSessionId, updatedAt }
 */
export const listActiveSessions = async () => {
  if (!db) throw new Error('Database not initialized');
  
  const ref = collection(db, 'sessionStates');
  const snap = await getDocs(ref);
  
  return snap.docs.map(d => {
    const data = d.data() || {};
    return {
      userId: d.id,
      email: data.email || null,
      displayName: data.displayName || null,
      activeSessionId: data.activeSessionId || null,
      updatedAt: data.updatedAt || null
    };
  });
};
